import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { KeycloakService } from './keycloakService';
import { AuthValidationService } from './authValidationService';
import { RetryService, RetryableRequest } from './retryService';
import { logHttpError } from './errorLogging/GlobalErrorLogger';

/**
 * Lista ścieżek, które nie wymagają autoryzacji
 */
const PUBLIC_PATHS = [
  '/login',
  '/reset-password-request',
  '/reset-password',
  '/register',
  '/confirm-register'
];

/**
 * Sprawdza czy aktualna ścieżka jest publiczna
 */
const isPublicPath = (): boolean => {
  const currentPath = window.location.pathname;
  return PUBLIC_PATHS.includes(currentPath);
};

/**
 * Common request interceptor logic
 */
const createRequestInterceptor = (context: string = '') => {
  return async (config: InternalAxiosRequestConfig) => {
    // DODAJ TO NA POCZĄTKU FUNKCJI:
    // Sprawdź czy to jest publiczna ścieżka - jeśli tak, nie dodawaj autoryzacji
    if (isPublicPath()) {
      return config;
    }

    // Check if token is valid before sending request
    if (!KeycloakService.isTokenValid(60)) { // 60 seconds safety margin
      try {
        console.log(`Token wymaga odświeżenia przed wysłaniem żądania${context ? ` (${context})` : ''}`);
        const refreshed = await KeycloakService.updateToken(60);
        if (refreshed) {
          console.log(`Token został odświeżony przed wysłaniem żądania${context ? ` (${context})` : ''}`);
        } else {
          console.warn('Token nie został odświeżony, ale może być nadal ważny');
        }
      } catch (error) {
        console.warn('Nie można odświeżyć tokenu przed żądaniem:', error);
        if (error instanceof Error) {
          console.warn('Szczegóły błędu:', error.message);
        }
        // Continue with request despite refresh failure - response interceptor will handle potential 401
      }
    }

    // Add current token to header
    const token = KeycloakService.getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn('Brak tokenu do dodania do żądania');
    }

    return config;
  };
};

/**
 * Common response interceptor logic
 */
const createResponseInterceptor = (context: string = '') => {
  return async (error: AxiosError) => {
    // DODAJ TO NA POCZĄTKU:
    // Nie obsługuj błędów autoryzacji na stronach publicznych
    if (isPublicPath()) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as RetryableRequest & { _retry?: boolean };

    // DODAJ TO NA POCZĄTKU:
    // Sprawdź czy żądanie już jest przetwarzane przez retry
    if (originalRequest._retryCount && originalRequest._retryCount > 0) {
      console.log(`Request already being retried, skipping interceptor retry (${context})`);
      return Promise.reject(error);
    }

    // Handle authentication errors (401) with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        console.log(`Otrzymano błąd 401, próba odświeżenia tokenu...${context ? ` (${context})` : ''}`);
        
        // Check if this is a token refresh error that requires logout
        if (AuthValidationService.isLogoutRequiredError(error)) {
          console.warn('Błąd wymaga wylogowania użytkownika');
          KeycloakService.login();
          return new Promise(() => {});
        }

        // Try to refresh token - use minValidity = -1 to force refresh
        const refreshed = await KeycloakService.updateToken(-1);

        if (refreshed && originalRequest.headers) {
          // Got new token, update Authorization header
          const newToken = KeycloakService.getToken();
          if (newToken) {
            console.log(`Token został odświeżony po błędzie 401${context ? ` (${context})` : ''}`);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            // Reset retry info before retrying with new token
            RetryService.resetRetryInfo(originalRequest);
            // Retry original request with new token
            return axios(originalRequest);
          }
        } else {
          // Failed to refresh token, need to re-authenticate
          console.log('Nie udało się odświeżyć tokenu, przekierowanie do logowania...');
          const currentLocation = window.location.pathname + window.location.search;
          sessionStorage.setItem('redirect_after_login', currentLocation);
          KeycloakService.login(currentLocation);
          return new Promise(() => {});
        }
      } catch (refreshError) {
        console.error('Błąd podczas odświeżania tokenu:', refreshError);
        
        // Check if refresh error requires logout
        if (AuthValidationService.isLogoutRequiredError(refreshError)) {
          console.warn('Błąd odświeżania wymaga wylogowania');
          KeycloakService.logout();
        } else {
          KeycloakService.login();
        }
        return new Promise(() => {});
      }
    }

    // NIE PONAWIAJ ŻADNYCH REQUESTÓW - loguj błąd i wyświetl na ekranie
    if (error.response?.status !== 401 && error.response?.status !== 403) {
      // Użyj GlobalErrorLogger do wyświetlenia błędu na ekranie
      logHttpError(error, context);
    }

    // For other errors or when retries are exhausted, continue with promise rejection
    return Promise.reject(error);
  };
};

/**
 * Funkcja tworząca instancję Axios z interceptorami do obsługi autoryzacji
 */
export const createAuthAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Use shared interceptor logic
  instance.interceptors.request.use(
    createRequestInterceptor('instance'),
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    createResponseInterceptor('instance')
  );

  return instance;
};

/**
 * Ustawia interceptory na globalnej instancji Axios
 */
export const setupGlobalAxiosInterceptors = (): void => {
  // Interceptor dla żądań - dodaje token autoryzacji przed każdym żądaniem
  axios.interceptors.request.use(
    async (config) => {
      // Sprawdź czy token jest ważny przed wysłaniem żądania
      // Używamy bardziej restrykcyjnego sprawdzenia z marginesem bezpieczeństwa
      if (!KeycloakService.isTokenValid(60)) { // 60 sekund marginesu
        try {
          console.log('Token wymaga odświeżenia przed wysłaniem żądania (global)');
          const refreshed = await KeycloakService.updateToken(60);
          if (refreshed) {
            console.log('Token został odświeżony przed wysłaniem żądania (global)');
          } else {
            console.warn('Token nie został odświeżony, ale może być nadal ważny (global)');
          }
        } catch (error) {
          console.warn('Nie można odświeżyć tokenu przed żądaniem (global):', error);
        }
      }

      // Dodajemy aktualny token do nagłówka
      const token = KeycloakService.getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn('Brak tokenu do dodania do żądania (global)');
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptor dla odpowiedzi - obsługuje błędy autoryzacji i retry logic
  axios.interceptors.response.use(
    (response) => response,
    createResponseInterceptor('global')
  );
};
